import { useState } from "react";
import { Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { clsx } from "clsx";
import agentsToGoLogo from "@assets/AGENTS-02_1_1778254256444.webp";
import { Seo, faqPageSchema, breadcrumbSchema } from "@/lib/seo";

const FAQS = [
  {
    q: "What exactly is AgentsToGo?",
    a: "AgentsToGo is a done-for-you AI agent building service. You describe what you need automated, and we build a custom AI agent tailored to your business — fully deployed and ready to use.",
  },
  {
    q: "Is there really a 100% money-back guarantee?",
    a: "Yes. If we can't deliver a working agent that meets the agreed scope, you get a full refund — no questions asked.",
  },
  {
    q: "What kinds of agents can you build?",
    a: "We build agents for automation, customer support, lead enrichment, content moderation, data processing, Discord bots, Slack assistants, invoice processing, and much more. If it can be automated with AI, we can build it.",
  },
  {
    q: "What tools and platforms do you integrate with?",
    a: "We integrate with the tools you already use — Slack, Discord, Notion, Airtable, Google Sheets, Zapier, n8n, OpenAI, and many others. Just let us know your stack.",
  },
  {
    q: "How long does it take to build my agent?",
    a: "Most agents are designed, built, and delivered within 5–10 business days after the discovery call, depending on complexity.",
  },
  {
    q: "Do I need any technical knowledge?",
    a: "None at all. You describe the problem in plain language and we handle everything — architecture, development, testing, and deployment.",
  },
  {
    q: "Who owns the agent once it's built?",
    a: "You do. All deliverables, code, and configurations are yours. We hand everything over at the end of the engagement.",
  },
  {
    q: "What happens after the agent is delivered?",
    a: "We offer a support window after delivery to handle any fine-tuning. For ongoing changes and maintenance, we have retainer packages available.",
  },
  {
    q: "How do I get started?",
    a: "Just chat with us here on the homepage. Describe what you need and our AI will guide you through the discovery process. Once we understand your use case, you can book your kickoff call directly.",
  },
  {
    q: "Do I need a technical team?",
    a: "No. We handle implementation, integrations, deployment, and setup for you. Your team just needs to describe the problem.",
  },
  {
    q: "Can the agents integrate with our existing tools?",
    a: "Yes. We support APIs, CRMs, databases, automation systems, and internal business tools — whatever is already in your stack.",
  },
  {
    q: "Are humans still involved in the process?",
    a: "Absolutely. Human review, approvals, and escalation layers can be added wherever needed. AI handles the repetitive work; your team stays in control.",
  },
];

export default function FAQsPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="min-h-screen scifi-wave-bg flex flex-col text-white">
      <Seo
        title="AI Agent FAQs | AgentsToGo — Custom AI Agent Service"
        description="Answers to common questions about AgentsToGo: what we build, how long it takes (typically 5–10 business days), pricing, integrations, ownership, and our 100% money-back guarantee."
        path="/faqs"
        jsonLd={[
          faqPageSchema(FAQS),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "FAQs", path: "/faqs" },
          ]),
        ]}
      />

      {/* ── NAV ── */}
      <div className="sticky top-0 z-[200] px-4 sm:px-6 py-3" style={{ background: "rgba(8,6,18,0.72)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(192,38,211,0.15)" }}>
        <nav
          className="flex justify-between items-center px-5 sm:px-6 py-2.5 w-full max-w-6xl mx-auto rounded-full"
          style={{ backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", background: "rgba(8,6,18,0.65)", border: "1px solid rgba(192,38,211,0.22)", boxShadow: "0 0 0 1px rgba(0,229,255,0.05), 0 8px 32px rgba(0,0,0,0.35)" }}
        >
          <Link href="/" className="flex items-center gap-2.5 hover:opacity-75 transition-opacity">
            <div className="flex flex-row items-center gap-2">
              <img src={agentsToGoLogo} alt="AgentsToGo" className="h-6 sm:h-7 w-auto object-contain" />
              <p className="text-[0.75rem] font-semibold tracking-tight leading-none" style={{ fontFamily: "'Hanken Grotesk', sans-serif", color: "#cbd5e1" }}>
                The w<span style={{ color: "#22d3ee", fontWeight: 700 }}>AI</span> to go
              </p>
            </div>
          </Link>
          <div className="flex items-center gap-6 text-sm font-medium">
            <Link href="/how-it-works" className="hidden sm:block text-slate-300 hover:text-cyan-300 transition-colors">How it Works</Link>
            <Link href="/about" className="hidden text-slate-300 hover:text-cyan-300 transition-colors">About</Link>
            <Link href="/faqs" className="hidden sm:block text-cyan-300 transition-colors">FAQs</Link>
          </div>
        </nav>
      </div>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden px-6 py-24 lg:px-12 border-b border-cyan-400/10">
        <div className="absolute -top-20 right-1/4 w-96 h-96 bg-fuchsia-500/12 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mb-6 inline-flex rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-fuchsia-300"
            style={{ border: "1px solid rgba(192,38,211,0.3)", background: "rgba(192,38,211,0.08)" }}
          >
            Got Questions?
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="text-4xl sm:text-6xl font-bold leading-tight tracking-tight"
          >
            Frequently Asked{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-fuchsia-300">
              Questions
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mx-auto mt-6 max-w-xl text-base leading-7 text-slate-300"
          >
            Everything you need to know about AgentsToGo — how it works, what we build, and what to expect.
          </motion.p>
        </div>
      </section>

      {/* ── FAQ ACCORDION ── */}
      <section className="flex-1 px-6 py-16 lg:px-12">
        <div className="mx-auto max-w-3xl">
          <div className="divide-y divide-cyan-400/10 rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(0,229,255,0.13)" }}>
            {FAQS.map((faq, i) => (
              <div key={i} style={{ background: "rgba(8,6,18,0.65)" }}>
                <button
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left group"
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                >
                  <span className={clsx(
                    "text-[15px] leading-snug font-medium transition-colors",
                    openIndex === i ? "text-cyan-300" : "text-slate-200 group-hover:text-white"
                  )}>
                    {faq.q}
                  </span>
                  <ChevronDown
                    className={clsx(
                      "w-4 h-4 shrink-0 transition-transform duration-200",
                      openIndex === i ? "rotate-180 text-cyan-300" : "text-slate-500"
                    )}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {openIndex === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-5 text-sm leading-relaxed text-slate-300">
                        {faq.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* CTA nudge */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mt-10 rounded-2xl px-6 py-6 text-center"
            style={{ background: "rgba(0,229,255,0.05)", border: "1px solid rgba(0,229,255,0.15)" }}
          >
            <p className="text-sm text-slate-300 mb-4">
              Still have questions? Chat with us directly on the homepage.
            </p>
            <Link
              href="/"
              className="inline-flex rounded-2xl bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold px-6 py-2.5 text-sm transition-colors"
              style={{ boxShadow: "0 0 20px rgba(0,229,255,0.3)" }}
            >
              Chat with Bluna →
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <div className="border-t border-cyan-400/10 px-6 sm:px-12 py-8 text-center" style={{ background: "rgba(0,0,0,0.4)" }}>
        <a href="mailto:bluna@agentstogo.com" className="inline-flex items-center gap-2 text-sm font-medium text-cyan-300 hover:text-cyan-200 hover:underline transition-colors">
          bluna@agentstogo.com
        </a>
        <p className="text-xs text-slate-500 mt-4">AgentsToGo · {new Date().getFullYear()}</p>
      </div>
    </div>
  );
}
